import { Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { MaintenanceProvider } from "@/context/MaintenanceContext";
import { MaintenanceOverlay } from "@/components/maintenance/MaintenanceOverlay";
import { MaintenanceWrapper } from "@/components/maintenance/MaintenanceWrapper";

// Layouts
import MarketingLayout from "@/layouts/MarketingLayout";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import FeaturesPage from "@/pages/FeaturesPage";
import FeatureDetailPage from "@/pages/FeatureDetailPage";
import PricingPage from "@/pages/PricingPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import FAQPage from "@/pages/FAQPage";
import DemoPage from "@/pages/DemoPage";
// import ResourcesPage from "@/pages/ResourcesPage";
// import ResourceDetailPage from "@/pages/ResourceDetailPage";
// import CaseStudiesPage from "@/pages/CaseStudiesPage";
// import CaseStudyDetailPage from "@/pages/CaseStudyDetailPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Lazy-loaded feature pages for better performance
import { lazy } from "react";
const PerformancePage = lazy(() => import("@/pages/features/PerformancePage"));
const SecurityPage = lazy(() => import("@/pages/features/SecurityPage"));
const AISolutionsPage = lazy(() => import("@/pages/features/AISolutionsPage"));
const GlobalCDNPage = lazy(() => import("@/pages/features/GlobalCDNPage"));
const DevelopmentPage = lazy(() => import("@/pages/features/DevelopmentPage"));
const AnalyticsPage = lazy(() => import("@/pages/features/AnalyticsPage"));
const AccessControlPage = lazy(
  () => import("@/pages/features/AccessControlPage"),
);
const RealTimePage = lazy(() => import("@/pages/features/RealTimePage"));

function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem
      data-oid=":ci82d3"
    >
      <MaintenanceProvider data-oid="3qdss2u">
        <MaintenanceOverlay data-oid="w.d97cx" />
        <MaintenanceWrapper data-oid="6w9rns5">
          <Routes data-oid="69aibb5">
            <Route
              path="/"
              element={<MarketingLayout data-oid="5emmpa5" />}
              data-oid="4e463.o"
            >
              <Route
                index
                element={<HomePage data-oid="_urlmk0" />}
                data-oid="0j.j0sj"
              />

              <Route
                path="about"
                element={<AboutPage data-oid="d3ch-92" />}
                data-oid="7ubp-kf"
              />

              <Route
                path="contact"
                element={<ContactPage data-oid="916t7lm" />}
                data-oid="8h276i_"
              />

              <Route
                path="features"
                element={<FeaturesPage data-oid="fh0le1x" />}
                data-oid="vt7ydrv"
              />

              {/* Specialized Feature Pages */}
              <Route
                path="features/performance"
                element={<PerformancePage data-oid="h1l.-3u" />}
                data-oid="rf_-f:m"
              />

              <Route
                path="features/security"
                element={<SecurityPage data-oid="wsnwrw-" />}
                data-oid="zja5jlv"
              />

              <Route
                path="features/ai-solutions"
                element={<AISolutionsPage data-oid="w:t4zak" />}
                data-oid="gs40-uv"
              />

              <Route
                path="features/global-cdn"
                element={<GlobalCDNPage data-oid="icqv55j" />}
                data-oid="ig3zcqu"
              />

              <Route
                path="features/development"
                element={<DevelopmentPage data-oid="8delu42" />}
                data-oid="p5j.tmd"
              />

              <Route
                path="features/analytics"
                element={<AnalyticsPage data-oid="9fggkqs" />}
                data-oid="hruxqky"
              />

              <Route
                path="features/access-control"
                element={<AccessControlPage data-oid="k3g:nj2" />}
                data-oid=".koocc8"
              />

              <Route
                path="features/real-time"
                element={<RealTimePage data-oid="ktqrb0-" />}
                data-oid="b5.t5rm"
              />

              {/* Generic Feature Detail Page (fallback) */}
              <Route
                path="features/:slug"
                element={<FeatureDetailPage data-oid="h4n.cgd" />}
                data-oid="w59qles"
              />

              <Route
                path="pricing"
                element={<PricingPage data-oid="5s88p26" />}
                data-oid="kop-jpz"
              />

              <Route
                path="blog"
                element={<BlogPage data-oid="vdvqkvr" />}
                data-oid="4yukpy3"
              />

              <Route
                path="blog/:slug"
                element={<BlogArticlePage data-oid="blog-article-route" />}
                data-oid="blog-article-wrapper"
              />

              {/**
               * Case Studies section temporarily disabled
               * <Route
               *   path="casestudy"
               *   element={<CaseStudiesPage data-oid="02_aj1t" />}
               *   data-oid="4p4c3d8"
               * />
               *
               * <Route
               *   path="casestudy/:slug"
               *   element={<CaseStudyDetailPage data-oid="case-study-route" />}
               *   data-oid="case-study-wrapper"
               * />
               */}

              <Route
                path="privacy"
                element={<PrivacyPage data-oid="7ew58-." />}
                data-oid="0nah426"
              />

              <Route
                path="terms"
                element={<TermsPage data-oid="u.v:ytu" />}
                data-oid="chiib.c"
              />

              <Route
                path="faq"
                element={<FAQPage data-oid="u09sk.:" />}
                data-oid="1dvakeu"
              />

              <Route
                path="demo"
                element={<DemoPage data-oid="y0ext1a" />}
                data-oid="9-87-mr"
              />

              {/**
               * Resources section temporarily disabled
               * <Route
               *   path="resources"
               *   element={<ResourcesPage data-oid="oiiyc76" />}
               *   data-oid="r288pjs"
               * />
               *
               * <Route
               *   path="resources/:slug"
               *   element={<ResourceDetailPage data-oid="resource-detail-route" />}
               *   data-oid="resource-detail-wrapper"
               * />
               */}
            </Route>
            <Route
              path="*"
              element={<NotFoundPage data-oid="zs2snm2" />}
              data-oid="-w721am"
            />
          </Routes>
        </MaintenanceWrapper>
        <Toaster richColors position="top-right" data-oid="mot7lb3" />
      </MaintenanceProvider>
    </ThemeProvider>
  );
}

export default App;
